/**
 * Rezept-Select-Zelle in Wizard-Schritt 2: Standard-Prompt / eigene Vorlage /
 * GOKI-Standard-Tool / "+ Neuer Prompt" (Spec 2026-08-15 §4.1). Aus
 * ChannelFieldRow.tsx extrahiert, um dessen 100-Zeilen-Limit einzuhalten.
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Select, type SelectOption } from '@/components/v2/ui'
import type { BulkAttributeCode } from '@/lib/api/channel-content-bulk'

import { InlineTemplateForm } from './InlineTemplateForm'
import type { FieldSelection } from './useFieldSelections'

const NO_RECIPE_VALUE = '__none__'
const NEW_PROMPT_VALUE = '__new__'
const TOOL_VALUE_PREFIX = 'tool:'

interface RecipeSelectCellProps {
  channelId: string
  channelLabel: string
  code: BulkAttributeCode
  field: FieldSelection
  templateOptions: SelectOption[]
  toolOptions?: SelectOption[]
  onFieldChange: (patch: Partial<FieldSelection>) => void
}

export function RecipeSelectCell({
  channelId,
  channelLabel,
  code,
  field,
  templateOptions,
  toolOptions = [],
  onFieldChange,
}: RecipeSelectCellProps) {
  const { t } = useTranslation()
  const [creatingNew, setCreatingNew] = useState(false)
  const fieldLabel = t(`channelContent.card.attribute.${code}`)

  if (creatingNew) {
    return (
      <InlineTemplateForm
        channelLabel={channelLabel}
        fieldLabel={fieldLabel}
        onCancel={() => setCreatingNew(false)}
        onCreated={(templateId) => {
          setCreatingNew(false)
          onFieldChange({ templateId, systemToolCode: null })
        }}
      />
    )
  }

  const recipeOptions: SelectOption[] = [
    { value: NO_RECIPE_VALUE, label: t('channelContent.wizard.recipeDefaultOption') },
    ...templateOptions,
    ...toolOptions.map((o) => ({ value: `${TOOL_VALUE_PREFIX}${o.value}`, label: o.label })),
    { value: NEW_PROMPT_VALUE, label: t('channelContent.wizard.newPromptOption') },
  ]

  return (
    <Select
      options={recipeOptions}
      value={
        field.systemToolCode
          ? `${TOOL_VALUE_PREFIX}${field.systemToolCode}`
          : (field.templateId ?? NO_RECIPE_VALUE)
      }
      onValueChange={(value) => {
        if (value === NEW_PROMPT_VALUE) {
          setCreatingNew(true)
        } else if (value === NO_RECIPE_VALUE) {
          onFieldChange({ templateId: null, systemToolCode: null })
        } else if (value.startsWith(TOOL_VALUE_PREFIX)) {
          onFieldChange({ templateId: null, systemToolCode: value.slice(TOOL_VALUE_PREFIX.length) })
        } else {
          onFieldChange({ templateId: value, systemToolCode: null })
        }
      }}
      aria-label={t('channelContent.wizard.recipeLabel')}
      id={`text-bulk-wizard-channel-${channelId}-field-${code}-recipe-select`}
    />
  )
}
